import { type FC, type PropsWithChildren, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import type { createClientKernel } from "../../Kernel/ClientKernel.ts";
import { useUiI18n, type UiTranslationKey } from "../../I18n";
import {
	runMatchLobbyPreload,
	runStartupPreload,
	type StartupCardPreview,
	type StartupPreloadProgress,
} from "./startupPreloader.ts";
import "./startupAnimation.css";

const INTRO_THRESHOLD_MS = 250;
const MINIMUM_INTRO_DURATION_MS = 650;
const EXIT_DURATION_MS = 350;

const delay = (duration: number) =>
	new Promise<void>((resolve) => {
		window.setTimeout(resolve, duration);
	});

type StartupAnimationProps = PropsWithChildren<{
	kernel: ReturnType<typeof createClientKernel>;
}>;

const CARD_POSITIONS = [
	{ x: "-150%", y: "14%", exitX: "-310%", rotation: "-20deg", delay: "0ms" },
	{ x: "-76%", y: "-7%", exitX: "-190%", rotation: "-10deg", delay: "110ms" },
	{ x: "0%", y: "-15%", exitX: "0%", rotation: "0deg", delay: "220ms" },
	{ x: "76%", y: "-7%", exitX: "190%", rotation: "10deg", delay: "330ms" },
	{ x: "150%", y: "14%", exitX: "310%", rotation: "20deg", delay: "440ms" },
] as const;

const STATUS_TRANSLATIONS: Readonly<Record<string, UiTranslationKey>> = {
	"Gathering arcane energy": "startup.gathering",
	"Opening the grimoire": "startup.opening",
	"Waking the battlefield": "startup.waking",
	"Gathering game artwork": "startup.artwork",
	"Reading the runes": "startup.reading",
	"Preparing the first hand": "startup.preparing",
	"The table is ready": "startup.ready",
};

export const StartupAnimation: FC<StartupAnimationProps> = ({ children, kernel }) => {
	const { t } = useUiI18n();
	const location = useLocation();
	const dashboardLoaded = useRef(false);
	const lobbyLoaded = useRef(false);
	const [cards, setCards] = useState<StartupCardPreview[]>([]);
	const [preloadError, setPreloadError] = useState(false);
	const [retryVersion, setRetryVersion] = useState(0);
	const [loading, setLoading] = useState<StartupPreloadProgress>({
		progress: 0,
		status: "Gathering arcane energy",
	});
	const [phase, setPhase] = useState<"pending" | "loading" | "exiting" | "complete">("pending");

	useEffect(() => {
		const isDashboard = location.pathname === "/dashboard";
		const isLobby = /^\/matches\/[^/]+\/lobby\/?$/.test(location.pathname);
		if (
			(!isDashboard && !isLobby) ||
			(isDashboard && dashboardLoaded.current) ||
			(isLobby && lobbyLoaded.current)
		) {
			setPhase("complete");
			return;
		}
		let active = true;
		setPhase("pending");
		setPreloadError(false);
		const onProgress = (nextLoading: StartupPreloadProgress) => {
			if (active) setLoading(nextLoading);
		};
		const preload = (
			isLobby
				? runMatchLobbyPreload({ onProgress })
				: runStartupPreload({
						client: kernel.client,
						onCards: (nextCards) => {
							if (active) setCards(nextCards);
						},
						onProgress,
					})
		)
			.then(() => true)
			.catch((error: unknown) => {
				kernel.logger.warn("Startup preload was incomplete", error);
				if (active) setPreloadError(true);
				return false;
			});

		const finishIntro = async () => {
			const completedBeforeThreshold = await Promise.race([
				preload,
				delay(INTRO_THRESHOLD_MS).then(() => false),
			]);
			if (!active) return;
			if (completedBeforeThreshold) {
				if (isDashboard) dashboardLoaded.current = true;
				lobbyLoaded.current = true;
				setPhase("complete");
				return;
			}

			const startedAt = performance.now();
			setPhase("loading");
			const succeeded = await preload;
			if (!succeeded) return;
			const elapsed = performance.now() - startedAt;
			if (elapsed < MINIMUM_INTRO_DURATION_MS) {
				await delay(MINIMUM_INTRO_DURATION_MS - elapsed);
			}
			if (!active) return;
			if (isDashboard) dashboardLoaded.current = true;
			lobbyLoaded.current = true;
			setPhase("exiting");
			await delay(EXIT_DURATION_MS);
			if (active) setPhase("complete");
		};
		void finishIntro();

		return () => {
			active = false;
		};
	}, [kernel, location.pathname, retryVersion]);

	useEffect(() => {
		if (phase === "pending" || phase === "complete") return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [phase]);

	const translatedStatus = (() => {
		const fixedKey = STATUS_TRANSLATIONS[loading.status];
		if (fixedKey) return t(fixedKey);
		const cardsAnswered = /^(\d+) cards answered the call$/.exec(loading.status);
		if (cardsAnswered) return t("startup.cardsAnswered", { count: cardsAnswered[1] });
		const enchanting = /^Enchanting card art · (\d+)\/(\d+)$/.exec(loading.status);
		if (enchanting) {
			return t("startup.enchanting", { loaded: enchanting[1], total: enchanting[2] });
		}
		return loading.status;
	})();

	return (
		<>
			<div
				aria-hidden={phase === "loading" || phase === "exiting"}
				className="startup-app-underlay"
			>
				{children}
			</div>
			{phase === "loading" || phase === "exiting" ? (
				<div
					className={`startup-animation startup-animation--${phase}`}
					data-testid="startup-animation"
				>
					<div aria-hidden className="startup-animation__aurora" />
					<div aria-hidden className="startup-animation__dust" />
					<div aria-hidden className="startup-animation__sigil">
						<div className="startup-animation__ring startup-animation__ring--outer" />
						<div className="startup-animation__ring startup-animation__ring--inner" />
						<div className="startup-animation__gem">
							<span>M</span>
						</div>
					</div>
					<div aria-hidden className="startup-animation__cards">
						{CARD_POSITIONS.map((position, index) => {
							const card = cards[index];
							return (
								<div
									className="startup-animation__card-shell"
									key={card?.id ?? index}
									style={
										{
											"--card-x": position.x,
											"--card-y": position.y,
											"--card-exit-x": position.exitX,
											"--card-rotation": position.rotation,
											"--card-delay": position.delay,
										} as React.CSSProperties
									}
								>
									<div className={`startup-animation__card ${card ? "is-revealed" : ""}`}>
										<div className="startup-animation__card-back">
											<span>M</span>
										</div>
										<div className="startup-animation__card-face">
											{card ? <img alt="" src={card.imageUrl} /> : null}
											<span>{card?.name}</span>
										</div>
									</div>
								</div>
							);
						})}
					</div>
					<div className="startup-animation__copy">
						<p className="startup-animation__eyebrow">{t("app.tagline")}</p>
						<h1>{t("app.title")}</h1>
						<div
							aria-label={translatedStatus}
							aria-valuemax={100}
							aria-valuemin={0}
							aria-valuenow={Math.round(loading.progress * 100)}
							className="startup-animation__progress"
							role="progressbar"
						>
							<span style={{ transform: `scaleX(${loading.progress})` }} />
						</div>
						<p aria-live="polite" className="startup-animation__status">
							{preloadError ? t("startup.loadFailed") : translatedStatus}
						</p>
						{preloadError ? (
							<button
								type="button"
								className="startup-animation__retry"
								onClick={() => setRetryVersion((value) => value + 1)}
							>
								{t("page.retry")}
							</button>
						) : null}
					</div>
				</div>
			) : null}
		</>
	);
};
